import { useState } from 'react';

import { LANGS, LANG_CODES, LANG_NAMES, type Concept, type Example, type Lang } from '@lexio/shared';

import { updateConcept, type ConceptEdit } from '../lib/api';

interface Props {
  concept: Concept;
  onSaved: (concept: Concept) => void;
  onCancel: () => void;
}

interface TermDraft {
  term: string;
  variants: string;
  note: string;
}

/** Varianty se pisou do jednoho pole, oddelene carkou nebo strednikem. */
function splitVariants(raw: string): string[] {
  return raw
    .split(/[;,]/)
    .map((v) => v.trim())
    .filter(Boolean);
}

/** Cestina a anglictina jsou v editoru vzdy - jsou to hlavni jazyky drilu. */
const ALWAYS: Lang[] = ['en', 'cs'];

/**
 * Rucni uprava konceptu: lepsi preklad, varianty, poznamka a vlastni priklady.
 * Ulozeni koncept oznaci jako upraveny, takze ho import obsahu neprepise.
 */
export function ConceptEditor({ concept, onSaved, onCancel }: Props) {
  const initialLangs = LANGS.filter((l) => ALWAYS.includes(l) || concept.terms[l]);
  const [langs, setLangs] = useState<Lang[]>(initialLangs);
  const [terms, setTerms] = useState<Partial<Record<Lang, TermDraft>>>(() =>
    Object.fromEntries(
      initialLangs.map((l) => {
        const t = concept.terms[l];
        return [l, { term: t?.term ?? '', variants: t?.variants.join(', ') ?? '', note: t?.note ?? '' }];
      }),
    ),
  );
  const [examples, setExamples] = useState<Example[]>(concept.examples.map((ex) => ({ ...ex })));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addable = LANGS.filter((l) => !langs.includes(l));

  const setTerm = (lang: Lang, patch: Partial<TermDraft>) =>
    setTerms((prev) => ({ ...prev, [lang]: { ...{ term: '', variants: '', note: '' }, ...prev[lang], ...patch } }));

  const setExample = (i: number, lang: Lang, text: string) =>
    setExamples((prev) => prev.map((ex, j) => (j === i ? { ...ex, [lang]: text } : ex)));

  const save = async () => {
    setSaving(true);
    setError(null);
    const edit: ConceptEdit = { terms: {}, examples };
    for (const l of langs) {
      const d = terms[l];
      if (!d) continue;
      // Jazyk, ktery koncept nemel a zustal prazdny, neposilame - nic se nemeni.
      if (!d.term.trim() && !concept.terms[l]) continue;
      edit.terms[l] = { term: d.term.trim(), variants: splitVariants(d.variants), note: d.note.trim() || null };
    }
    try {
      const { concept: saved } = await updateConcept(concept.id, edit);
      onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Uložení se nepovedlo');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="concept-editor" onClick={(e) => e.stopPropagation()}>
      {langs.map((l) => {
        const d = terms[l] ?? { term: '', variants: '', note: '' };
        return (
          <fieldset className="edit-lang" key={l}>
            <legend>{LANG_NAMES[l]}</legend>
            <input
              className="input"
              placeholder={`Výraz (${LANG_CODES[l]})${concept.terms[l] ? ' – prázdné = odebrat' : ''}`}
              value={d.term}
              onChange={(e) => setTerm(l, { term: e.target.value })}
            />
            <input
              className="input"
              placeholder="Další významy / synonyma, oddělené čárkou"
              value={d.variants}
              onChange={(e) => setTerm(l, { variants: e.target.value })}
            />
            <textarea
              className="input"
              rows={2}
              placeholder="Poznámka – co to vlastně znamená, kdy se to říká…"
              value={d.note}
              onChange={(e) => setTerm(l, { note: e.target.value })}
            />
          </fieldset>
        );
      })}

      {addable.length > 0 && (
        <select
          className="input"
          value=""
          onChange={(e) => {
            const l = e.target.value as Lang;
            if (l) setLangs((prev) => LANGS.filter((x) => prev.includes(x) || x === l));
          }}
        >
          <option value="">+ Přidat jazyk…</option>
          {addable.map((l) => (
            <option key={l} value={l}>
              {LANG_NAMES[l]}
            </option>
          ))}
        </select>
      )}

      <div className="edit-examples">
        <div className="lang-label">Příklady</div>
        {examples.map((ex, i) => (
          <div className="edit-example" key={i}>
            {langs.map((l) => (
              <input
                key={l}
                className="input"
                placeholder={`Věta (${LANG_CODES[l]})`}
                value={ex[l] ?? ''}
                onChange={(e) => setExample(i, l, e.target.value)}
              />
            ))}
            <button
              className="btn ghost small"
              onClick={() => setExamples((prev) => prev.filter((_, j) => j !== i))}
            >
              Odebrat příklad
            </button>
          </div>
        ))}
        <button className="btn ghost small" onClick={() => setExamples((prev) => [...prev, {}])}>
          + Přidat příklad
        </button>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="row">
        <button className="btn primary" disabled={saving} onClick={() => void save()}>
          {saving ? 'Ukládám…' : 'Uložit'}
        </button>
        <button className="btn ghost" disabled={saving} onClick={onCancel}>
          Zrušit
        </button>
      </div>
    </div>
  );
}
